import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Envelope, CalendarCheck, ClockCounterClockwise, FileText, Warning, Sparkle, ArrowRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function Stat({ Icon, label, value, hint, tone = "default", testid }) {
  const toneCls = tone === "warn" ? "text-[#FF3B30]" : "text-neutral-950 dark:text-neutral-50";
  return (
    <div data-testid={testid} className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-5 bg-white dark:bg-neutral-900 hover:shadow-md hover:-translate-y-[2px] transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">{label}</div>
        <Icon size={18} className="text-neutral-500 dark:text-neutral-500" weight="duotone" />
      </div>
      <div className={`mt-4 text-4xl font-semibold ${toneCls}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      {hint && <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    const r = await api.get("/dashboard/stats");
    setStats(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runDemo = async () => {
    setSeeding(true);
    try {
      await api.post("/demo/seed");
      const r = await api.post("/demo/sample-enquiry");
      toast.success(`New enquiry: ${r.data.customer_name}`);
      await load();
    } catch (e) {
      toast.error("Failed to generate sample enquiry");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Overview</div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Dashboard</h1>
        </div>
        <button
          data-testid="dashboard-simulate-btn"
          onClick={runDemo}
          disabled={seeding}
          className="text-sm px-4 py-2 rounded-md bg-[#0F52BA] hover:bg-[#0C4094] disabled:opacity-60 text-white transition-colors flex items-center gap-2"
        >
          <Sparkle size={16} weight="fill" />
          {seeding ? "Generating…" : "Simulate customer enquiry"}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 dark:text-neutral-500">Loading…</div>
      ) : (
        <>
          <motion.div
            initial="hidden" animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
            className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4"
          >
            {[
              { Icon: CalendarCheck, label: "Today's appts", value: stats.today_appointments, testid: "stat-today" },
              { Icon: ClockCounterClockwise, label: "Pending approvals", value: stats.pending_approvals, testid: "stat-pending" },
              { Icon: Envelope, label: "New enquiries", value: stats.new_enquiries, testid: "stat-new" },
              { Icon: FileText, label: "Draft quotes", value: stats.draft_quotes, testid: "stat-quotes" },
              { Icon: Envelope, label: "Unread msgs", value: stats.unread_messages, testid: "stat-unread" },
              { Icon: Warning, label: "AI low-confidence", value: stats.low_confidence_alerts, tone: stats.low_confidence_alerts > 0 ? "warn" : "default", testid: "stat-lowconf" },
            ].map((s) => (
              <motion.div key={s.label} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}>
                <Stat {...s} />
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Recent activity</div>
                <Link to="/inbox" className="text-xs text-[#0F52BA] flex items-center gap-1 hover:underline">Open inbox <ArrowRight size={12} /></Link>
              </div>
              {stats.recent_activity.length === 0 ? (
                <div className="text-sm text-neutral-500 dark:text-neutral-500 py-8 text-center">Nothing yet. Click &ldquo;Simulate customer enquiry&rdquo; to see the AI work.</div>
              ) : (
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {stats.recent_activity.map((a) => (
                    <li key={a.activity_id} className="py-3 flex items-center justify-between">
                      <div className="text-sm">{a.message}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-500">{new Date(a.created_at).toLocaleTimeString()}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-4">How it works</div>
              <ol className="text-sm space-y-3 text-neutral-700 dark:text-neutral-300">
                <li><span className="text-neutral-400 dark:text-neutral-500 mr-2">01</span> Enquiry arrives (Gmail / Messenger)</li>
                <li><span className="text-neutral-400 dark:text-neutral-500 mr-2">02</span> AI analyses intent & drafts reply</li>
                <li><span className="text-neutral-400 dark:text-neutral-500 mr-2">03</span> AI checks calendar & suggests slots</li>
                <li><span className="text-neutral-400 dark:text-neutral-500 mr-2">04</span> You approve → we send.</li>
              </ol>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
